import { ArrowRight, CalendarDays, ChevronLeft } from 'lucide-react';
import { type ReactNode, useId, useMemo, useRef, useState } from 'react';
import { ApiError } from '../../api/errors';
import { useActiveUsers, useUserMap } from '../../api/org';
import { useUpdateTask } from '../../api/tasks';
import type { Task, UpdateTaskInput, User } from '../../api/types';
import { useSession } from '../../app/session';
import { HelpHint } from '../training/HelpHint';
import { Avatar } from '../../components/ui/Avatar';
import { Button } from '../../components/ui/Button';
import { type Option, SearchSelect } from '../../components/ui/Combobox';
import { DatePanel } from '../../components/ui/DatePicker';
import { Field, FormAlert, Textarea } from '../../components/ui/Field';
import type { Placement } from '../../components/ui/layer';
import { Popover } from '../../components/ui/Popover';
import { useToast } from '../../components/ui/Toast';
import { useTooltip } from '../../components/ui/Tooltip';
import { cn } from '../../lib/cn';
import { formatDate, formatDateFull } from '../../lib/dates';
import { DueLabel } from './TaskBits';
import { lateAssignSuggestion } from './taskUtils';

export function useUserOptions(opts: { excludeIds?: string[] } = {}): Option[] {
  const users = useActiveUsers();
  const { userId } = useSession();
  return useMemo(
    () =>
      users
        .filter((u) => !opts.excludeIds?.includes(u.id))
        .sort((a, b) => (a.id === userId ? -1 : b.id === userId ? 1 : a.name.localeCompare(b.name, 'uk')))
        .map((u) => ({
          value: u.id,
          label: u.id === userId ? `${u.name} (я)` : u.name,
          description: u.jobTitle ?? u.email,
          keywords: `${u.email} ${u.businessRoleCodes.join(' ')}`,
          icon: <Avatar name={u.name} src={u.avatarUrl} size={20} />,
        })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [users, userId, (opts.excludeIds ?? []).join(',')],
  );
}

/** Помилки сервера для кроку з причиною: detail + повідомлення полів дослівно. */
function ServerError({ error, known = [] }: { error: unknown; known?: string[] }) {
  if (!error) return null;
  if (error instanceof ApiError) {
    const extra = error.errors.filter((e) => !known.includes(e.field)).map((e) => e.message);
    return <FormAlert detail={error.detail} items={extra} />;
  }
  return <FormAlert detail="Не вдалося зберегти зміни." />;
}

type ReasonStepProps = {
  title: string;
  summary: ReactNode;
  reasonLabel: string;
  reasonRequired: boolean;
  submitLabel: string;
  onBack?: () => void;
  onSubmit: (reason: string) => void;
  pending: boolean;
  error: unknown;
  extra?: ReactNode;
  reasonHint?: string;
  className?: string;
};

/** Крок «причина зміни»: обов'язковий за політикою фірми для перенесення строку й зміни виконавця. */
export function ReasonStep({ title, summary, reasonLabel, reasonRequired, submitLabel, onBack, onSubmit, pending, error, extra, reasonHint, className }: ReasonStepProps) {
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const localError = touched && reasonRequired && reason.trim().length < 3 ? 'Вкажіть причину (щонайменше 3 символи).' : null;
  const serverField = error instanceof ApiError ? error.fieldError('changeReason') : undefined;
  const titleId = useId();
  return (
    <form
      noValidate
      aria-labelledby={titleId}
      className={cn('flex flex-col gap-3', className ?? 'w-[340px] max-w-[calc(100vw-16px)] p-3')}
      onSubmit={(e) => {
        e.preventDefault();
        setTouched(true);
        if (reasonRequired && reason.trim().length < 3) return;
        onSubmit(reason.trim());
      }}
    >
      <div className="flex items-center gap-1">
        {onBack && (
          <button type="button" onClick={onBack} aria-label="Назад" className="-ml-1 inline-flex size-7 items-center justify-center rounded-sm text-fg-3 hover:bg-surface-2">
            <ChevronLeft className="size-4" aria-hidden />
          </button>
        )}
        <h3 id={titleId} className="text-body font-semibold">
          {title}
        </h3>
      </div>
      <div className="rounded-sm bg-surface-2 px-2.5 py-2 text-meta text-fg-2">{summary}</div>
      {extra}
      <Field label={reasonLabel} required={reasonRequired} requiredNote="за правилами фірми" error={localError ?? serverField} hint={reasonHint} aside={<HelpHint id="task.reason" />}>
        <Textarea
          data-autofocus
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              e.currentTarget.form?.requestSubmit();
            }
          }}
          placeholder="Наприклад: клієнт надав документи пізніше"
        />
      </Field>
      <ServerError error={error} known={['changeReason']} />
      <div className="flex justify-end gap-2">
        {onBack && (
          <Button variant="ghost" size="sm" onClick={onBack}>
            Скасувати
          </Button>
        )}
        <Button type="submit" variant="primary" size="sm" loading={pending}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

type ControlVariant = 'row' | 'field' | 'chip';

/** Строк задачі з перенесенням: вибір дати → причина (якщо строк уже був і правило вимагає). */
export function DueDateControl({
  task,
  variant = 'row',
  disabled,
  placement = 'bottom-end',
  className,
}: {
  task: Task;
  variant?: ControlVariant;
  disabled?: boolean;
  placement?: Placement;
  className?: string;
}) {
  const { policy } = useSession();
  const update = useUpdateTask();
  const toast = useToast();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [pendingDate, setPendingDate] = useState<string | null | undefined>(undefined);
  const btn = useRef<HTMLButtonElement>(null);
  const tip = useTooltip(task.dueOn ? `Строк: ${formatDateFull(task.dueOn)}, перенести` : 'Призначити строк', { describe: false });

  const close = () => {
    setAnchor(null);
    setPendingDate(undefined);
    update.reset();
  };

  const save = (dueOn: string | null, reason?: string) => {
    const body: UpdateTaskInput = { dueOn };
    if (reason) body.changeReason = reason;
    update.mutate(
      { task, body },
      {
        onSuccess: () => {
          close();
          toast.success(dueOn ? `Строк ${task.key}: ${formatDate(dueOn)}` : `Строк ${task.key} знято`);
        },
        onError: (e) => {
          if (e instanceof ApiError && e.status === 412) {
            close();
            toast.error('Задачу щойно змінив інший працівник', 'Дані оновлено — перегляньте й повторіть перенесення.');
            return;
          }
          // Сервер вимагає причину або відхилив дату — показуємо крок із причиною й поясненням.
          setPendingDate(dueOn);
        },
      },
    );
  };

  const onPick = (d: string | null) => {
    if (d === task.dueOn) return close();
    const needsReason = !!task.dueOn && policy.requireReasonOnDueChange;
    if (!needsReason) save(d);
    else setPendingDate(d);
  };

  const trigger =
    variant === 'field' ? (
      <span className="inline-flex items-center gap-2">
        <CalendarDays className="size-4 text-fg-3" aria-hidden />
        <DueLabel task={task} className="text-body" />
      </span>
    ) : variant === 'chip' ? (
      <DueLabel task={task} showEmpty />
    ) : (
      <DueLabel task={task} showEmpty={false} />
    );

  return (
    <>
      <button
        ref={btn}
        type="button"
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={!!anchor}
        aria-label={task.dueOn ? `Строк ${task.key}: ${formatDateFull(task.dueOn)}, змінити` : `Призначити строк ${task.key}`}
        onClick={(e) => {
          e.stopPropagation();
          setAnchor(anchor ? null : e.currentTarget);
        }}
        {...(variant === 'row' ? tip.triggerProps : {})}
        className={cn(
          'inline-flex min-h-7 items-center rounded-sm px-1.5 text-left transition-colors hover:bg-surface-3 disabled:cursor-default disabled:hover:bg-transparent',
          variant === 'field' && 'min-h-8 -ml-1.5',
          variant === 'row' && !task.dueOn && 'text-fg-3',
          className,
        )}
      >
        {trigger}
        {variant === 'row' && !task.dueOn && <CalendarDays className="size-4 text-fg-3" aria-hidden />}
      </button>
      {variant === 'row' && tip.element}
      <Popover open={!!anchor} onClose={close} anchor={anchor} placement={placement} aria-label={`Строк ${task.key}`} className="max-w-[calc(100vw-16px)]">
        {pendingDate === undefined ? (
          <DatePanel value={task.dueOn} onSelect={onPick} allowClear={!!task.dueOn} clearLabel="Зняти строк" />
        ) : (
          <ReasonStep
            title="Перенесення строку"
            summary={
              <span className="tnum flex flex-wrap items-center gap-1.5">
                <span>{task.dueOn ? formatDate(task.dueOn) : 'Без строку'}</span>
                <ArrowRight className="size-3.5" aria-hidden />
                <span className="font-medium text-fg">{pendingDate ? formatDate(pendingDate) : 'Без строку'}</span>
              </span>
            }
            reasonLabel="Причина перенесення"
            reasonRequired={!!task.dueOn && policy.requireReasonOnDueChange}
            reasonHint="Причину побачать учасники задачі; вона збережеться в історії змін."
            submitLabel="Перенести строк"
            onBack={() => {
              setPendingDate(undefined);
              update.reset();
            }}
            onSubmit={(reason) => save(pendingDate, reason)}
            pending={update.isPending}
            error={update.error}
          />
        )}
      </Popover>
    </>
  );
}

/** Виконавець із зміною: вибір людини → причина (якщо правило вимагає) і пропозиція строку після 15:00. */
export function AssigneeControl({
  task,
  variant = 'row',
  disabled,
  placement = 'bottom-end',
  className,
}: {
  task: Task;
  variant?: ControlVariant;
  disabled?: boolean;
  placement?: Placement;
  className?: string;
}) {
  const { policy, userId } = useSession();
  const users = useUserMap();
  const options = useUserOptions();
  const update = useUpdateTask();
  const toast = useToast();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [stepAnchor, setStepAnchor] = useState<HTMLElement | null>(null);
  const [pending, setPending] = useState<User | null>(null);
  const [moveDue, setMoveDue] = useState(true);
  const assignee = task.assigneeId ? users.get(task.assigneeId) : undefined;
  const tip = useTooltip(assignee ? `Виконавець: ${assignee.name}` : 'Призначити виконавця', { describe: false });

  const closeAll = () => {
    setAnchor(null);
    setStepAnchor(null);
    setPending(null);
    update.reset();
  };

  const suggestion = pending ? lateAssignSuggestion(policy, pending.id, userId, task.dueOn) : null;

  const save = (u: User, reason?: string, newDue?: string) => {
    const body: UpdateTaskInput = { assigneeId: u.id };
    if (newDue) body.dueOn = newDue;
    if (reason) body.changeReason = reason;
    update.mutate(
      { task, body },
      {
        onSuccess: () => {
          closeAll();
          toast.success(`Виконавець ${task.key}: ${u.name}`, newDue ? `Строк: ${formatDate(newDue)}` : undefined);
        },
        onError: (e) => {
          if (e instanceof ApiError && e.status === 412) {
            closeAll();
            toast.error('Задачу щойно змінив інший працівник', 'Дані оновлено — повторіть зміну виконавця.');
            return;
          }
          setPending(u);
        },
      },
    );
  };

  const onPick = (id: string) => {
    const u = users.get(id);
    if (!u || id === task.assigneeId) return;
    const needsReason = !!task.assigneeId && policy.requireReasonOnReassign;
    const late = lateAssignSuggestion(policy, u.id, userId, task.dueOn);
    const el = anchor;
    setAnchor(null);
    if (!needsReason && !late) {
      save(u);
      return;
    }
    setMoveDue(true);
    setPending(u);
    setStepAnchor(el);
  };

  const reasonNeeded = !!task.assigneeId && policy.requireReasonOnReassign;
  const dueReasonNeeded = !!suggestion && moveDue && !!task.dueOn && policy.requireReasonOnDueChange;

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={!!anchor}
        aria-label={assignee ? `Виконавець ${task.key}: ${assignee.name}, змінити` : `Призначити виконавця ${task.key}`}
        onClick={(e) => {
          e.stopPropagation();
          setAnchor(anchor ? null : e.currentTarget);
        }}
        {...(variant === 'row' ? tip.triggerProps : {})}
        className={cn(
          'inline-flex min-h-7 min-w-0 items-center gap-2 rounded-sm px-1 text-left transition-colors hover:bg-surface-3 disabled:cursor-default disabled:hover:bg-transparent',
          variant === 'field' && 'min-h-8 -ml-1',
          className,
        )}
      >
        {assignee ? <Avatar name={assignee.name} src={assignee.avatarUrl} size={variant === 'field' ? 24 : 22} /> : <Avatar empty size={variant === 'field' ? 24 : 22} />}
        {variant !== 'row' && (
          <span className={cn('truncate text-body', !assignee && 'text-fg-3')}>{assignee ? assignee.name : 'Не призначено'}</span>
        )}
      </button>
      {variant === 'row' && tip.element}
      <SearchSelect
        open={!!anchor}
        onClose={() => setAnchor(null)}
        anchor={anchor}
        placement={placement}
        options={options}
        value={task.assigneeId}
        onSelect={onPick}
        label={`Виконавець ${task.key}`}
        placeholder="Пошук працівника"
        emptyText="Нікого не знайдено"
        header={
          <p className="border-b border-line px-3 py-2 text-caption text-fg-3">
            Одна задача — один виконавець.{reasonNeeded ? ' Зміна — з причиною.' : ''}
          </p>
        }
      />
      <Popover open={!!pending && !!stepAnchor} onClose={closeAll} anchor={stepAnchor} placement={placement} aria-label="Зміна виконавця">
        {pending && (
          <ReasonStep
            title="Зміна виконавця"
            summary={
              <span className="flex flex-wrap items-center gap-1.5">
                <span>{assignee?.name ?? 'Не призначено'}</span>
                <ArrowRight className="size-3.5" aria-hidden />
                <span className="font-medium text-fg">{pending.name}</span>
              </span>
            }
            extra={
              suggestion ? (
                <div className="rounded-sm border border-info-line bg-info-soft px-2.5 py-2 text-meta text-info-fg">
                  <p>
                    Після {policy.lateAssignHour}:00 доручення іншому працівнику ставлять не раніше ніж на наступний робочий день.
                  </p>
                  <label className="mt-1.5 flex items-center gap-2 font-medium">
                    <input type="checkbox" checked={moveDue} onChange={(e) => setMoveDue(e.target.checked)} className="size-3.5 accent-[var(--c-accent)]" />
                    Перенести строк на {formatDate(suggestion)}
                  </label>
                </div>
              ) : null
            }
            reasonLabel={reasonNeeded ? 'Причина зміни виконавця' : 'Причина (для історії змін)'}
            reasonRequired={reasonNeeded || dueReasonNeeded}
            submitLabel="Змінити виконавця"
            onBack={closeAll}
            onSubmit={(reason) => save(pending, reason || undefined, suggestion && moveDue ? suggestion : undefined)}
            pending={update.isPending}
            error={update.error}
          />
        )}
      </Popover>
    </>
  );
}
